import type { ReferralStatus } from '@/hooks/useReferrals';

export type ReferralSortColumn = 'name' | 'referrer' | 'received' | 'status' | 'assigned';
export type ReferralSortDirection = 'asc' | 'desc';

export interface ReferralListSort {
  column: ReferralSortColumn;
  direction: ReferralSortDirection;
}

/** Newest received referral first, matching the list before a column is clicked. */
export const DEFAULT_REFERRAL_LIST_SORT: ReferralListSort = {
  column: 'received',
  direction: 'desc',
};

export interface ReferralSortRow {
  id: string;
  first_name: string;
  last_name: string;
  referrer_name: string | null;
  source: string;
  received_at: string;
  status: ReferralStatus;
  assigned_clinician_id: string | null;
}

const TEXT: Intl.CollatorOptions = { sensitivity: 'base', numeric: true };

const STATUS_ORDER: Record<ReferralStatus, number> = {
  new: 0,
  accepted: 1,
  declined: 2,
};

/**
 * Clicking the active column flips direction. Clicking another column
 * starts at A→Å / oldest / low→high.
 */
export function nextReferralListSort(
  current: ReferralListSort,
  column: ReferralSortColumn,
): ReferralListSort {
  if (current.column === column) {
    return { column, direction: current.direction === 'asc' ? 'desc' : 'asc' };
  }
  return { column, direction: 'asc' };
}

/** Primary referrer text. A MedCom referral without a name sorts as the badge. */
export function referralReferrerSortLabel(
  row: Pick<ReferralSortRow, 'referrer_name' | 'source'>,
  medcomLabel: string,
): string {
  const name = row.referrer_name?.trim();
  if (name) return name;
  if (row.source === 'medcom') return medcomLabel;
  return '';
}

export function sortReferrals<T extends ReferralSortRow>(
  rows: readonly T[],
  sort: ReferralListSort,
  options: {
    assigneeLabel: (row: T) => string;
    medcomLabel: string;
  },
): T[] {
  return [...rows].sort((a, b) => compareReferralListItems(a, b, sort, options));
}

export function compareReferralListItems<T extends ReferralSortRow>(
  a: T,
  b: T,
  sort: ReferralListSort,
  options: {
    assigneeLabel: (row: T) => string;
    medcomLabel: string;
  },
): number {
  let cmp = 0;
  switch (sort.column) {
    case 'name':
      cmp = compareText(a.last_name, b.last_name, sort.direction);
      if (cmp === 0) cmp = compareText(a.first_name, b.first_name, sort.direction);
      break;
    case 'referrer':
      cmp = compareText(
        referralReferrerSortLabel(a, options.medcomLabel),
        referralReferrerSortLabel(b, options.medcomLabel),
        sort.direction,
      );
      break;
    case 'received':
      cmp = compareTime(
        receivedMillis(a.received_at),
        receivedMillis(b.received_at),
        sort.direction,
      );
      break;
    case 'status':
      cmp = compareRank(STATUS_ORDER[a.status], STATUS_ORDER[b.status], sort.direction);
      break;
    case 'assigned':
      cmp = compareText(options.assigneeLabel(a), options.assigneeLabel(b), sort.direction);
      break;
    default: {
      const unexpected: never = sort.column;
      return unexpected;
    }
  }
  if (cmp !== 0) return cmp;
  return tie(a, b);
}

/** Newest received first, then id. Not flipped when the column direction flips. */
function tie(a: ReferralSortRow, b: ReferralSortRow): number {
  const received = compareReceivedDesc(a.received_at, b.received_at);
  if (received !== 0) return received;
  return a.id.localeCompare(b.id);
}

function compareReceivedDesc(a: string, b: string): number {
  const aTime = receivedMillis(a);
  const bTime = receivedMillis(b);
  if (aTime == null && bTime == null) return 0;
  if (aTime == null) return 1;
  if (bTime == null) return -1;
  return bTime - aTime;
}

function receivedMillis(value: string | null | undefined): number | null {
  if (!value) return null;
  const time = new Date(value).getTime();
  return Number.isNaN(time) ? null : time;
}

/** Missing values stay last in both directions. */
function compareText(
  a: string | null | undefined,
  b: string | null | undefined,
  direction: ReferralSortDirection,
): number {
  const aText = blankToNull(a);
  const bText = blankToNull(b);
  if (aText === null && bText === null) return 0;
  if (aText === null) return 1;
  if (bText === null) return -1;
  const cmp = aText.localeCompare(bText, 'da', TEXT);
  return direction === 'asc' ? cmp : -cmp;
}

function compareTime(a: number | null, b: number | null, direction: ReferralSortDirection): number {
  if (a == null && b == null) return 0;
  if (a == null) return 1;
  if (b == null) return -1;
  const cmp = a - b;
  return direction === 'asc' ? cmp : -cmp;
}

function compareRank(a: number, b: number, direction: ReferralSortDirection): number {
  const cmp = a - b;
  return direction === 'asc' ? cmp : -cmp;
}

function blankToNull(value: string | null | undefined): string | null {
  const trimmed = value?.trim() ?? '';
  if (trimmed.length === 0 || trimmed === '—' || trimmed === '–' || trimmed === '-') return null;
  return trimmed;
}
